import type { NovelItem } from '@app/_proto/Protos/novels';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import HomeCollection from '@app/domains/home/components/HomeCollection';
import HomeGenres from '@app/domains/home/components/HomeGenres';
import HomeRecentContent from '@app/domains/home/components/HomeRecentContent';
import HomeSwiper from '@app/domains/home/components/HomeSwiper';
import { useHomePopularSlidesPerView } from '@app/domains/home/containers/HomePopularNovels';
import {
    HOME_COLLECTION_TITLE_FONT_STYLE,
    HOME_SWIPER_SLIDE_SIZE_PER_VIEW_STYLE,
    HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE,
} from '@app/domains/home/styles';
import { breakpoints } from '@app/utils/breakpoints';
import { useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

type Props = {
    genres: string[] | undefined;
    genreContents: { genre?: string; genreNovels?: NovelItem[] | undefined[] }[];
    isSuccess?: boolean;
};

export default function HomePopularGenreNovels({ genres, genreContents, isSuccess }: Props) {
    const [selectedGenrePrev, setSelectedGenre] = useState(undefined);
    const selectedGenre = selectedGenrePrev || genres?.[0];

    const slidesPerView = useHomePopularSlidesPerView();
    const isDownLg = useMediaQuery(breakpoints.downLg, {
        noSsr: true,
    });

    return (
        <HomeCollection
            data-posthog-params={JSON.stringify({
                collectionSlot: HomeCollectionSlots.Genre,
                genre: selectedGenre,
            })}
            isLoading={!isSuccess}
        >
            <HomeCollection.Title title="Popular Genres" classes={{ heading: HOME_COLLECTION_TITLE_FONT_STYLE }} />
            <div className="pt-12 pb-8 md:pt-16">
                <HomeGenres
                    selectedGenre={selectedGenre}
                    genres={genres}
                    onSelect={genre => setSelectedGenre(genre)}
                    isLoading={!isSuccess}
                />
            </div>
            {genreContents?.map((genreContent, swiperIdx) => (
                <HomeSwiper
                    key={swiperIdx}
                    slidesPerView={slidesPerView}
                    slidesPerGroup={isDownLg ? 1 : 7}
                    className={twMerge(
                        HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE,
                        genreContent.genre !== selectedGenre && 'invisible h-0 !py-0 md:!py-0'
                    )}
                    isLoading={!isSuccess}
                >
                    {genreContent.genreNovels?.map((content: NovelItem | undefined, idx) => (
                        <HomeSwiper.Slide
                            key={idx}
                            className={HOME_SWIPER_SLIDE_SIZE_PER_VIEW_STYLE}
                            data-posthog-params={JSON.stringify({ itemSlot: idx })}
                        >
                            <HomePopularGenreContent content={content} isLoading={!isSuccess} />
                        </HomeSwiper.Slide>
                    ))}
                </HomeSwiper>
            ))}
        </HomeCollection>
    );
}

const HomePopularGenreContent = HomeRecentContent;
